import { useId, useState } from 'react'
import { inputClass } from './ui'

type Props = { files: File[]; onChange: (files: File[]) => void; maxImages: number; maxImageMb: number; disabled?: boolean; error?: string }
export const heicMessage = "HEIC photos aren't supported. Export as JPEG or PNG."

export default function PhotoUploadField({ files, onChange, maxImages, maxImageMb, disabled, error }: Props) {
  const id = useId()
  const [localError, setLocalError] = useState('')
  const message = localError || error
  return <div className="rounded-lg border border-dashed border-line bg-paper p-5">
    <label htmlFor={id} className="block text-sm font-semibold">Screen photos</label>
    <p id={`${id}-help`} className="mt-2 text-sm leading-6 text-muted">Whole screen, straight on, no glare; crop to the screen if you can. JPEG, PNG or WebP · up to {maxImages} photos, {maxImageMb} MB each.</p>
    <input id={id} type="file" name="screen_images[]" multiple accept="image/jpeg,image/png,image/webp" disabled={disabled}
      className={`${inputClass} mt-4 file:mr-3 file:rounded-sm file:border-0 file:bg-soft file:px-3 file:py-2 file:text-sm file:font-medium`}
      aria-invalid={!!message} aria-describedby={`${id}-help${message ? ` ${id}-error` : ''}`}
      onChange={event => {
        const selected = Array.from(event.target.files ?? [])
        let problem = ''
        if (selected.some(file => /\.(heic|heif)$/i.test(file.name) || ['image/heic', 'image/heif'].includes(file.type))) problem = heicMessage
        else if (selected.some(file => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type))) problem = 'Photos must be JPEG, PNG or WebP images.'
        else if (selected.length > maxImages) problem = `Choose up to ${maxImages} photos.`
        else if (selected.some(file => file.size > maxImageMb * 1024 * 1024)) problem = `Each photo must be smaller than ${maxImageMb} MB.`
        setLocalError(problem)
        onChange(problem ? [] : selected)
        if (problem) event.target.value = ''
      }} />
    {message && <p role="alert" id={`${id}-error`} className="mt-3 text-sm text-alert-ink">{message}</p>}
    {files.length > 0 && <ul className="mt-3 space-y-1 break-words text-xs text-muted" aria-label="Selected photos">{files.map((file, index) => <li key={`${file.name}-${index}`}>{index + 1}. {file.name}</li>)}</ul>}
  </div>
}
